// BRN-2123 — matching a route or a live pathname against a declared surface's `routePattern`.
//
// Two different comparisons live here and conflating them is the bug this module exists to avoid:
//
//  - PATTERN vs ROUTE (`patternMatchesRoute`) compares the registry against `code-routes.json`, and
//    both sides are ABSTRACT. `{meterId}` in the declaration and `:meterId` in the inventory are the
//    same segment, and the param NAMES are deliberately not compared — the route tree names its
//    folder `[meterId]` while the declaration's `routePattern` is written for a human reading the
//    engine's registration, and forcing those two to agree would be a coupling with no benefit.
//  - PATTERN vs PATHNAME (`patternMatchesPathname`) compares the registry against a CONCRETE URL at
//    runtime, where `{meterId}` matches exactly one real segment.
//
// Segment counts must be equal in both cases: a prefix match would make `/portal` swallow every
// screen under it, which is precisely how a registry stops meaning anything.

/** A `{param}` token in a declared `routePattern`. */
const PATTERN_PARAM = /^\{[^/{}]+\}$/;
/** A `:param` token in the route inventory's paths (the producer's normalized form). */
const ROUTE_PARAM = /^:[^/]+$/;

export interface RoutePatternHolder {
  readonly id: string;
  readonly routePattern: string;
}

function segments(path: string): string[] {
  return path.split("/").filter((segment) => segment.length > 0);
}

/** True when the segment is a wildcard on the declaration side. */
function isPatternParam(segment: string): boolean {
  return PATTERN_PARAM.test(segment);
}

/** True when the segment is a wildcard on the inventory side. */
function isRouteParam(segment: string): boolean {
  return ROUTE_PARAM.test(segment);
}

/** Declaration pattern vs an inventory route path: a param matches a param, a literal matches the
 *  same literal. */
export function patternMatchesRoute(routePattern: string, routePath: string): boolean {
  const pattern = segments(routePattern);
  const route = segments(routePath);
  if (pattern.length !== route.length) {
    return false;
  }
  return pattern.every((segment, index) => {
    const other = route[index];
    return isPatternParam(segment) ? isRouteParam(other) : segment === other;
  });
}

/** Declaration pattern vs a live pathname: a param matches exactly one concrete segment. */
export function patternMatchesPathname(routePattern: string, pathname: string): boolean {
  const pattern = segments(routePattern);
  const actual = segments(stripQuery(pathname));
  if (pattern.length !== actual.length) {
    return false;
  }
  return pattern.every((segment, index) => (isPatternParam(segment) ? actual[index].length > 0 : segment === actual[index]));
}

function stripQuery(pathname: string): string {
  const cut = pathname.search(/[?#]/);
  return cut === -1 ? pathname : pathname.slice(0, cut);
}

/**
 * The surface a live pathname is on, or null when the product declares none for it (which is a
 * CLEARING push, never a guess). When more than one pattern matches, the most SPECIFIC wins —
 * fewest params first, then the longest literal — so a concrete `/portal/meters/readings` is never
 * captured by `/portal/meters/{meterId}`.
 */
export function findSurfaceForPathname<T extends RoutePatternHolder>(
  surfaces: readonly T[],
  pathname: string,
): T | null {
  const matches = surfaces.filter((surface) => patternMatchesPathname(surface.routePattern, pathname));
  if (matches.length === 0) {
    return null;
  }
  return [...matches].sort((left, right) => {
    const byParams = paramCount(left.routePattern) - paramCount(right.routePattern);
    return byParams !== 0 ? byParams : right.routePattern.length - left.routePattern.length;
  })[0];
}

function paramCount(routePattern: string): number {
  return segments(routePattern).filter(isPatternParam).length;
}

/**
 * The concrete values a live pathname supplied for the pattern's `{param}` segments, in order.
 * Returns an empty array when the pattern does not match, so a caller cannot silently read a
 * neighbouring route's segment as an identifier.
 */
export function extractRouteParams(routePattern: string, pathname: string): string[] {
  if (!patternMatchesPathname(routePattern, pathname)) {
    return [];
  }
  const pattern = segments(routePattern);
  const actual = segments(stripQuery(pathname));
  return pattern.flatMap((segment, index) => (isPatternParam(segment) ? [actual[index]] : []));
}
